import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Smartphone, AlertTriangle, MoonStar } from 'lucide-react';
import clsx from 'clsx';

/**
 * Timeline player for mobile app sessions recorded by the Fitnessage RN
 * replay SDK. Mobile sessions have no DOM, so instead of rrweb playback this
 * renders an animated interaction timeline on a phone frame: screens, touch
 * ripples, app-state changes and errors.
 *
 * Event types (mirrors mobile/lib/replay/config.ts in the fitnessage repo):
 * 100 session_meta, 101 screen, 102 touch, 103 app_state, 104 error,
 * 105 custom, 106 frame (downscaled JPEG of the actual screen — rendered as
 * the phone's display; sessions from app builds without frame capture fall
 * back to the route-name placeholder).
 */

export interface MobileEvent {
  type: number;
  timestamp: number;
  data: any;
}

export const MOBILE_EVENT_LABELS: Record<number, string> = {
  100: 'session start',
  101: 'screen',
  102: 'touch',
  103: 'app state',
  104: 'error',
  105: 'event',
  106: 'frame',
};

export function isMobileReplayEvent(type: unknown): boolean {
  return typeof type === 'number' && type >= 100 && type <= 199;
}

const SPEEDS = [1, 2, 4, 8];
/** Collapse idle gaps longer than this during playback (ms). */
const MAX_GAP_MS = 4000;

interface TimelinePoint {
  event: MobileEvent;
  /** Playback time (ms from start, with long gaps collapsed) */
  t: number;
  /** Real wall-clock offset from session start (ms) */
  real: number;
}

export default function MobileReplay({ events }: { events: MobileEvent[] }) {
  const sorted = useMemo(
    () => [...events].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)),
    [events],
  );

  const meta = useMemo(() => {
    const m = sorted.find((e) => e.type === 100)?.data || {};
    return {
      platform: m.platform || 'unknown',
      appVariant: m.appVariant || 'member',
      appVersion: m.appVersion || '',
      screenWidth: Number(m.screenWidth) || 390,
      screenHeight: Number(m.screenHeight) || 844,
    };
  }, [sorted]);

  // Build the playback timeline with long idle gaps collapsed.
  const timeline = useMemo<TimelinePoint[]>(() => {
    if (sorted.length === 0) return [];
    const t0 = sorted[0].timestamp;
    const points: TimelinePoint[] = [];
    let playback = 0;
    let prev = t0;
    for (const event of sorted) {
      const gap = Math.min(Math.max(event.timestamp - prev, 0), MAX_GAP_MS);
      playback += gap;
      prev = event.timestamp;
      points.push({ event, t: playback, real: event.timestamp - t0 });
    }
    return points;
  }, [sorted]);

  const duration = timeline.length ? timeline[timeline.length - 1].t : 0;

  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(0);

  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    lastTickRef.current = performance.now();
    const tick = (now: number) => {
      const dt = (now - lastTickRef.current) * speed;
      lastTickRef.current = now;
      setTime((t) => {
        const next = t + dt;
        if (next >= duration) {
          setPlaying(false);
          return duration;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, speed, duration]);

  // Derive current state at `time`.
  const current = useMemo(() => {
    let screen = '/';
    let backgrounded = false;
    let lastError: string | null = null;
    let frame: { b64: string; screen?: string } | null = null;
    const activeTouches: Array<{ nx: number; ny: number; age: number; key: number }> = [];

    for (let i = 0; i < timeline.length; i++) {
      const { event, t } = timeline[i];
      if (t > time) break;
      if (event.type === 101) screen = event.data?.path || screen;
      else if (event.type === 103) backgrounded = event.data?.state === 'background';
      else if (event.type === 104 && time - t < 3000) lastError = event.data?.message || 'Error';
      else if (event.type === 106 && event.data?.b64) frame = event.data;
      else if (event.type === 102) {
        const age = time - t;
        if (age < 900) {
          activeTouches.push({
            nx: Number(event.data?.nx) || 0,
            ny: Number(event.data?.ny) || 0,
            age,
            key: i,
          });
        }
      }
    }
    // A frame from a different screen than the current route is stale —
    // e.g. after navigating onto a frame-excluded (sensitive) screen. Hide
    // it rather than show the wrong screen's pixels.
    if (frame?.screen && frame.screen !== screen) frame = null;
    return { screen, backgrounded, lastError, frame, activeTouches };
  }, [time, timeline]);

  const hasFrames = useMemo(() => sorted.some((e) => e.type === 106), [sorted]);

  const fmt = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  if (timeline.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">
        No mobile replay events in this session
      </div>
    );
  }

  const aspect = meta.screenWidth / meta.screenHeight;

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-300">
        <Smartphone className="h-4 w-4" />
        <span className="font-medium capitalize">{meta.appVariant}</span>
        <span className="text-gray-500">
          {meta.platform} · v{meta.appVersion} · {meta.screenWidth}×{meta.screenHeight}
        </span>
      </div>

      {/* Phone frame */}
      <div className="flex flex-1 items-center justify-center overflow-hidden p-4">
        <div
          className="relative overflow-hidden rounded-[2rem] border-4 border-gray-700 bg-gray-800 shadow-2xl"
          style={{ height: '100%', aspectRatio: String(aspect) }}
        >
          {/* Actual screen pixels (frame capture) */}
          {current.frame && (
            <img
              src={`data:image/jpeg;base64,${current.frame.b64}`}
              alt={current.screen}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}

          {/* Placeholder when this session/screen has no frame to show */}
          {!current.frame && (
            <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
              <p className="text-[11px] leading-relaxed text-gray-500">
                {hasFrames
                  ? 'No visual for this screen (sensitive screens are not captured)'
                  : 'No screen frames in this session — recorded by an app build without frame capture'}
              </p>
            </div>
          )}

          {/* Screen name */}
          <div className="absolute inset-x-0 top-0 z-10 bg-gray-900/80 px-3 py-2 text-center">
            <p className="truncate font-mono text-xs text-gray-200">{current.screen}</p>
          </div>

          {/* Touch ripples */}
          {current.activeTouches.map((touch) => (
            <span
              key={touch.key}
              className="pointer-events-none absolute rounded-full border-2 border-brand-400 bg-brand-400/30"
              style={{
                left: `${touch.nx * 100}%`,
                top: `${touch.ny * 100}%`,
                width: 14 + (touch.age / 900) * 26,
                height: 14 + (touch.age / 900) * 26,
                opacity: 1 - touch.age / 900,
                transform: 'translate(-50%, -50%)',
              }}
            />
          ))}

          {/* Backgrounded overlay */}
          {current.backgrounded && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-900/80">
              <div className="text-center text-gray-400">
                <MoonStar className="mx-auto h-6 w-6" />
                <p className="mt-1 text-xs">App in background</p>
              </div>
            </div>
          )}

          {/* Error flash */}
          {current.lastError && (
            <div className="absolute inset-x-2 bottom-2 z-20 flex items-start gap-1.5 rounded-lg bg-red-900/90 px-2.5 py-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-300" />
              <p className="break-all text-[10px] leading-snug text-red-100">{current.lastError}</p>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="border-t border-gray-700 bg-gray-800 px-4 py-3">
        {/* Scrubber with markers */}
        <div className="relative mb-2 h-6">
          <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-gray-600">
            <div
              className="h-full rounded-full bg-brand-500"
              style={{ width: `${duration ? (time / duration) * 100 : 0}%` }}
            />
          </div>
          {timeline.map(({ event, t }, i) => {
            if (event.type !== 101 && event.type !== 104) return null;
            return (
              <span
                key={i}
                title={event.type === 101 ? event.data?.path : event.data?.message}
                className={clsx(
                  'absolute top-1/2 h-3 w-1 -translate-y-1/2 rounded-full',
                  event.type === 104 ? 'bg-red-400' : 'bg-blue-300',
                )}
                style={{ left: `${duration ? (t / duration) * 100 : 0}%` }}
              />
            );
          })}
          <input
            type="range"
            min={0}
            max={duration}
            value={time}
            onChange={(e) => setTime(Number(e.target.value))}
            className="absolute inset-0 w-full cursor-pointer opacity-0"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (!playing && time >= duration) setTime(0);
              setPlaying((p) => !p);
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white hover:bg-brand-700"
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
          </button>
          <span className="font-mono text-xs text-gray-300">
            {fmt(time)} / {fmt(duration)}
          </span>
          <div className="ml-auto flex gap-1">
            {SPEEDS.map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={clsx(
                  'rounded px-2 py-0.5 text-xs',
                  speed === s ? 'bg-brand-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600',
                )}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
